<template>
  <span :class="imageViewClass">
    <div
      :class="{
        'image-view__body--focused': selected,
        'image-view__body--resizing': resizing,
      }"
      class="image-view__body"
    >
      <img
        :src="src"
        :title="node.attrs.title"
        :alt="node.attrs.alt"
        :width="width"
        :height="height"
        class="image-view__body__image"
        @click="selectImage"
      />

      <div
        v-if="view.editable"
        v-show="selected || resizing"
        class="image-resizer"
      >
        <span
          v-for="direction in resizeDirections"
          :key="direction"
          :class="`image-resizer__handler--${direction}`"
          class="image-resizer__handler"
          @mousedown="onMouseDown($event, direction)"
        />
      </div>

      <!-- when image is break text or float
      bubble menu's position is miscalculated
      use el-popover instead bubble menu -->
      <MPopover v-if="view.editable" :open="selected" placement="top">
        <template v-slot:trigger>
          <div class="image-view__body__placeholder" />
        </template>
        <ImageBubbleButton
          :node="node"
          :view="view"
          :update-attrs="updateAttrs"
        />
      </MPopover>

      <portal v-if="isPreviewing" to="image-preview">
        <div class="image-preview">
          <div class="actions text-right mb-2">
            <MButton
              shape="circle"
              :shadow="false"
              variant="transparent"
              class="mr-2"
              @click="downloadImage"
            >
              <MIcon name="download" size="lg" class="text-white" />
            </MButton>
            <MButton
              shape="circle"
              variant="transparent"
              :shadow="false"
              @click="isPreviewing = false"
            >
              <MIcon name="times" size="lg" class="text-white" />
            </MButton>
          </div>
          <div class="img">
            <img :src="src" />
          </div>
        </div>
      </portal>
    </div>
  </span>
</template>

<script>
import { Node as ProsemirrorNode } from 'prosemirror-model'
import { NodeSelection } from 'prosemirror-state'
import { EditorView } from 'prosemirror-view'
import ImageBubbleButton from './image-bubble-button.vue'
// import ImageBubbleMenu from '../MenuBubble/ImageBubbleMenu.vue'

function clamp(val, min, max) {
  if (val < min) {
    return min
  }
  if (val > max) {
    return max
  }
  return val
}

function resolveImg(src) {
  return new Promise((resolve, reject) => {
    const result = {
      complete: false,
      width: 0,
      height: 0,
      src,
    }

    if (!src) {
      reject(result)
      return
    }

    const img = new Image()

    img.onload = () => {
      result.width = img.width
      result.height = img.height
      result.complete = true

      resolve(result)
    }

    img.onerror = () => {
      reject(result)
    }

    img.src = src
  })
}

const MIN_SIZE = 20
const MAX_SIZE = 100000

export default {
  name: 'ImageView',
  components: {
    ImageBubbleButton,
  },
  props: {
    node: {
      type: ProsemirrorNode,
      required: true,
    },
    view: {
      type: EditorView,
      required: true,
    },
    getPos: {
      type: Function,
      required: true,
    },
    updateAttrs: {
      type: Function,
      required: true,
    },
    selected: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      maxSize: {
        width: MAX_SIZE,
        height: MAX_SIZE,
      },
      originalSize: {
        width: 0,
        height: 0,
      },
      resizeOb: new ResizeObserver(() => {
        this.getMaxSize()
      }),
      resizeDirections: ['tl', 'tr', 'bl', 'br'],
      resizing: false,
      resizerState: {
        x: 0,
        y: 0,
        w: 0,
        h: 0,
        dir: '',
      },
      isPreviewing: false,
    }
  },
  computed: {
    src() {
      return this.node.attrs.src
    },
    width() {
      return this.node.attrs.width
    },
    height() {
      return this.node.attrs.height
    },

    display() {
      return this.node.attrs.display
    },

    imageViewClass() {
      return ['image-view', `image-view--${this.display}`]
    },
  },
  async created() {
    const result = await resolveImg(this.src)

    if (!result.complete) {
      result.width = MIN_SIZE
      result.height = MIN_SIZE
    }

    this.originalSize = {
      width: result.width,
      height: result.height,
    }
  },
  mounted() {
    this.resizeOb.observe(this.view.dom)
  },
  beforeDestroy() {
    this.resizeOb.disconnect()
  },
  methods: {
    downloadImage() {
      window.open(this.src, '__blank')
    },
    selectImage() {
      if (!this.view.editable) {
        this.isPreviewing = true
        return
      }
      const { state } = this.view
      let { tr } = state
      const selection = NodeSelection.create(state.doc, this.getPos())
      tr = tr.setSelection(selection)
      this.view.dispatch(tr)
    },
    getMaxSize() {
      const { width } = getComputedStyle(this.view.dom)
      this.maxSize.width = parseInt(width, 10)
    },
    /* on resizer handler mousedown
     * record the position where the event is triggered and resize direction
     * calculate the initial width and height of the image
     */
    onMouseDown(e, dir) {
      e.preventDefault()
      e.stopPropagation()

      this.resizerState.x = e.clientX
      this.resizerState.y = e.clientY

      const originalWidth = this.originalSize.width
      const originalHeight = this.originalSize.height
      const aspectRatio = originalWidth / originalHeight

      let { width, height } = this.node.attrs
      const maxWidth = this.maxSize.width

      if (width && !height) {
        width = width > maxWidth ? maxWidth : width
        height = Math.round(width / aspectRatio)
      } else if (height && !width) {
        width = Math.round(height * aspectRatio)
        width = width > maxWidth ? maxWidth : width
      } else if (!width && !height) {
        width = originalWidth > maxWidth ? maxWidth : originalWidth
        height = Math.round(width / aspectRatio)
      } else {
        width = width > maxWidth ? maxWidth : width
      }

      this.resizerState.w = width
      this.resizerState.h = height
      this.resizerState.dir = dir

      this.resizing = true

      this.onEvents()
    },

    onMouseMove(e) {
      e.preventDefault()
      e.stopPropagation()
      if (!this.resizing) return

      const { x, y, w, h, dir } = this.resizerState

      const dx = (e.clientX - x) * (/l/.test(dir) ? -1 : 1)
      const dy = (e.clientY - y) * (/t/.test(dir) ? -1 : 1)

      this.updateAttrs({
        width: clamp(w + dx, MIN_SIZE, this.maxSize.width),
        height: Math.max(h + dy, MIN_SIZE),
      })
    },

    onMouseUp(e) {
      e.preventDefault()
      e.stopPropagation()
      if (!this.resizing) return

      this.resizing = false

      this.resizerState = {
        x: 0,
        y: 0,
        w: 0,
        h: 0,
        dir: '',
      }

      this.offEvents()
      this.selectImage()
    },

    onEvents() {
      document.addEventListener('mousemove', this.onMouseMove, true)
      document.addEventListener('mouseup', this.onMouseUp, true)
    },

    offEvents() {
      document.removeEventListener('mousemove', this.onMouseMove, true)
      document.removeEventListener('mouseup', this.onMouseUp, true)
    },
  },
}
</script>
